"use client";

import { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default tracking-widest text-xs uppercase text-foreground hover:text-secondary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) return null;
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[400px] bg-background border-l border-border p-8">
        <div className="flex flex-col gap-8 mt-12">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="font-default tracking-widest text-xl uppercase text-foreground hover:text-secondary transition-colors block" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>
            </div>)}
          <div className="mt-8 pt-8 border-t border-border">
            <Button variant="outline" className="w-full border-secondary text-foreground hover:bg-secondary hover:text-secondary-foreground font-default tracking-widest uppercase text-sm h-12 rounded-md" onClick={() => setIsOpen(false)}>
              Get Started
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264695" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-secondary/30" : "border-b border-transparent"}`}>
      <MenuProvider menu_id="16">
        <div className="container mx-auto px-6 h-[80px] flex items-center justify-between">
          <div className="flex-1 flex justify-start">
            <Link to="/" className="flex-shrink-0">
              <WvcLogo className="h-8 w-auto text-foreground" />
            </Link>
          </div>

          <div className="flex-shrink-0">
            <DesktopNav />
          </div>

          <div className="flex-1 flex justify-end items-center gap-4">
            <div className="hidden lg:flex items-center gap-4 mr-4">
              <span className="font-default text-[10px] tracking-widest uppercase text-muted-foreground cursor-pointer hover:text-secondary transition-colors">EN</span>
              <span className="font-default text-[10px] tracking-widest uppercase text-muted-foreground cursor-pointer hover:text-secondary transition-colors">CZ</span>
              <span className="font-default text-[10px] tracking-widest uppercase text-muted-foreground cursor-pointer hover:text-secondary transition-colors">DE</span>
            </div>
            <Button variant="outline" className="hidden md:inline-flex border-secondary text-foreground hover:bg-secondary hover:text-secondary-foreground font-default tracking-widest uppercase text-[10px] px-6 h-10 rounded-md transition-all duration-300">
              Get Started
            </Button>
            <MobileNav />
          </div>
        </div>
      </MenuProvider>
    </section>;
}